// navigation.ts — single source of truth for sidebar navigation config.
//
// PersonaSidebar renders hub items from this array.
// Header.tsx derives breadcrumbs from this array + hubDefinitions.
// CommandPalette generates navigation entries from hubDefinitions.
// Never duplicate route→label mappings elsewhere.

import type { ComponentType } from 'react'
import {
  LayoutDashboard,
  Workflow,
  DollarSign,
  Settings,
  Shield,
  Server,
  Cpu,
  Network,
  Building2,
  Boxes,
  LibraryBig,
  UserCog,
  Megaphone,
  Trash2,
  Radio,
  Plug,
  Bell,
  Database,
} from 'lucide-react'
import type { Persona } from '@/hooks/usePersona'
import type { UserRole } from '@/types/auth'
import { getBuildConcept } from '@/components/build/buildConcepts'

/**
 * True when the persona holds one of `roles`.
 *
 * WHICH GATE TO USE: if the tab's route has a <RequireRole>, use `roleIn` with
 * that same list. Otherwise a Persona boolean is fine. A derived boolean covers a
 * role set that moves whenever PERMISSIONS changes, which is how several of these
 * gates drifted from their routes and showed tabs that then bounced the role;
 * navigation.rbac.test.tsx asserts the two sides agree.
 *
 * PLATFORM ROLES: a tab lists a platform role only where it already did. Platform
 * personas are denied another tenant's data by tenant_context.go unless a live
 * impersonation grant exists, so adding them here would advertise pages that 403,
 * and removing them from Capabilities/Compliance/Trust/Log Archive would undo a
 * fix made for that exact reason (CommandPalette.tsx, review of P2.439). Whether
 * platform staff should reach tenant settings from /tenant/* at all is an open
 * decision recorded in navigation.rbac.test.tsx's KNOWN_HIDDEN_HUBS, not
 * something to settle one predicate at a time.
 *
 * `!!p.role` is required, not redundant: Persona.role is `UserRole | null` and
 * `roles.includes(null)` does not type-check.
 */
const roleIn = (p: Persona, roles: readonly UserRole[]): boolean =>
  !!p.role && roles.includes(p.role)

/**
 * The tenant-plane roles the server grants ordinary tenant resource reads to —
 * workflow:list, agent_definition:list, agent_skill:list, team:list,
 * dagster:read, ai_chat_session:list, mcp_tool:list, ai_prompt:list. The golden
 * gives all eight of those the SAME four holders, which is why one constant
 * covers them rather than eight lists.
 *
 * The other five tenant-plane roles — tenant_billing_admin,
 * tenant_cost_center_admin, tenant_finance_viewer, approval_viewer and
 * approval_approver — hold NO read on any of them. A tab shown to those roles
 * is a dead end of the second kind: the route has no RequireRole to bounce
 * them, so the page mounts and its first request 403s into an error card.
 *
 * These tabs all read `p.isTenant`, which is true for every tenant-plane role.
 * That is the same defect #766 fixed on the 21 GUARDED tabs ("the tab gate was
 * p.isTenant while the route enumerates roles") — it simply could not be seen
 * on these, because with no RequireRole the dead-end check compared nothing.
 *
 * Source of truth: internal/authz/opa/testdata/role_matrix.golden, asserted
 * tab-by-tab in navigation.rbac.test.tsx's unguarded-tab checks. Change this
 * list only to follow a policy change, never to make a test pass.
 */
const TENANT_RESOURCE_READERS = [
  'tenant_owner',
  'tenant_admin',
  'tenant_operator',
  'tenant_viewer',
] as const satisfies readonly UserRole[]

export interface NavItem {
  name: string
  to: string
  icon: ComponentType<{ className?: string }>
  /** Capability check — item is shown only when this returns true. */
  visible: (persona: Persona) => boolean
  /** Top-level section label. Omit for ungrouped items (no header rendered). */
  section?: string
  /** Sub-section within a section. */
  subSection?: string
  /**
   * Pin this item to the bottom of the sidebar, directly above the
   * expand/collapse control, instead of placing it in the scrolling list.
   * For low-frequency, destructive-adjacent destinations that should stay
   * reachable without competing with everyday navigation.
   *
   * Visibility, routing and active-matching are unaffected — PersonaSidebar
   * renders footer items through the same component as list items, so they
   * keep the same RBAC gate, geometry and active-marker animation.
   */
  footer?: boolean
}

// ── Hub types ────────────────────────────────────────────────────────────────

/**
 * A single tab within a hub page (route-based pill tab).
 */
export interface HubTab {
  name: string
  /** Canonical route for this tab. */
  to: string
  /** Old names/synonyms findable in the command palette. */
  aliases?: string[]
  /** Persona gate — mirrors the RequireRole on the original route. */
  visible: (persona: Persona) => boolean
  /**
   * When true the tab navigates to a page outside the hub route tree
   * (e.g. Chat keeps its own layout at /tenant/chat; SSO stays at
   * /tenant/settings/sso until ux-settings moves it in T2.3).
   * HubTabs renders these as plain NavLinks; no hub Outlet child is needed.
   */
  external?: boolean
  /**
   * Exact-match this tab's route. Set on a hub's INDEX tab (whose `to` equals
   * the hub root) when sibling tabs are nested under it — otherwise the index
   * tab would also read as active on every sibling route.
   */
  end?: boolean
  /**
   * Optional visual group label (P2.432). Tabs are chunked into contiguous
   * runs sharing the same `group` value; HubTabs renders a divider + label
   * before each run and leaves this field's ordering (array order) alone —
   * grouping is purely presentational data, never a second source of tab
   * order. Omit for hubs that don't need grouping: HubTabs renders those
   * exactly as it always has (no group, no divider, no wrapper element).
   * A hub may mix grouped and ungrouped tabs; an ungrouped tab renders on
   * its own (a "run" of one) wherever it falls in the array.
   */
  group?: string
  /**
   * Optional one-line explanation shown as a tooltip on the tab (Stage 0,
   * BUILD-SURFACES-SIMPLIFICATION-SEP22.md §5/§7). Sourced from
   * components/build/buildConcepts.ts wherever a tab maps 1:1 onto a build
   * concept, so it can never drift from the Definitions kind switcher or
   * the "What's the difference?" panel's wording.
   */
  tooltip?: string
}

/**
 * Defines the tab set for one hub. Used by HubTabs, getBreadcrumbs,
 * and CommandPalette. `hubTo` must match the corresponding NavItem `to`.
 */
export interface HubDef {
  /** Must match the NavItem name for this hub. */
  hubName: string
  /** Must match the NavItem `to` for this hub. */
  hubTo: string
  /** Ordered list of tabs. Empty for hubs whose tabs belong to another agent. */
  tabs: HubTab[]
}

// ── Constants ────────────────────────────────────────────────────────────────

/**
 * localStorage namespace key for persisted section collapse state.
 * Use only this constant — never inline the string elsewhere.
 */
export const NAV_COLLAPSED_KEY = 'vharta.nav.collapsed'

// ── Hub definitions (tab-level config, single source of truth) ───────────────

/**
 * Tab definitions for every hub. Visibility functions mirror RequireRole
 * gates on the corresponding routes. `aliases` power command-palette search
 * so typing an old name (e.g. "executions", "audit log") finds the new home.
 */
export const hubDefinitions: HubDef[] = [
  {
    hubName: 'Build',
    hubTo: '/tenant/build',
    // P2.432: grouped as DATA only (3 groups — Build / Reuse / AI agents;
    // revised per the lead's "merge surfaces, not labels" ruling — Templates
    // stays single-workflow templates, "Automations" moved into the AI Use
    // cases surface instead of a Build-hub label change). The third group
    // was renamed "People & agents" → "AI agents" in the build-surfaces
    // Stage 0 pass (BUILD-SURFACES-SIMPLIFICATION-SEP22.md §7 decision 5 /
    // expert-build-ontology.md §5.3.1: the old label read like a role/
    // persona taxonomy when it names a Build-hub sub-section that is
    // exclusively AI entities — Agents/Skills/Teams). "people" is kept
    // command-palette-searchable as an alias on the Agents tab below.
    // HubTabs renders the dividers/labels generically from `group`; this
    // array's ORDER is still the single source of tab order (unchanged from
    // before grouping). `group` values are intentionally plain hub-scoped
    // strings, not tied to any particular grouping scheme.
    //
    // P2.435 (UX-consult verdict): the planned Workflows/Agent Graphs/
    // Chains/Runbooks list-with-facet unification landed — those four are
    // now ONE tab, routed at /tenant/build/workflows, backed by a `?kind=`
    // facet shell (DefinitionsPage, components/build/definitionKinds.ts)
    // instead of four separate hub tabs/routes. The Agent Graphs/Chains/
    // Runbooks tabs are REMOVED from the strip; their old routes now
    // redirect to the matching `?kind=` facet (App.tsx). Their aliases move
    // onto this tab so the command palette still finds them under their old
    // names.
    //
    // P2.435 follow-up (UX-consult review of b2b8fe3d1): the tab was first
    // named "Workflows" while the shell it opens renders <h2>Definitions</h2>
    // — a name mismatch between the strip and the page it opens. Renamed the
    // tab to "Definitions" to match; 'workflow'/'workflows' are kept as
    // aliases (alongside every other retired tab's aliases) precisely
    // because they are no longer the literal tab name, and CommandPalette's
    // match is a plain substring check over [hubName, tab.name, ...aliases]
    // (see components/command-palette/CommandPalette.tsx's `includesQuery`)
    // — losing the exact word "workflows" from that haystack would make a
    // tenant's own search for "workflows" fail to find this tab.
    tabs: [
      {
        name: 'Definitions',
        to: '/tenant/build/workflows',
        aliases: [
          'workflow',
          'workflows',
          'automation flow',
          // Carried over from the retired Agent Graphs tab.
          'agent graph',
          'agent graphs',
          'stategraph',
          'state graph',
          'graph',
          // Carried over from the retired Chains tab.
          'chain',
          'chains',
          'pipeline',
          // Carried over from the retired Runbooks tab.
          'runbook',
          'runbooks',
        ],
        visible: (p) => roleIn(p, TENANT_RESOURCE_READERS),
        group: 'Build',
        // Stage 0: the tab covers four kinds with four different one-liners
        // (see the Definitions kind switcher itself for the per-kind
        // breakdown) — the tab-level tooltip stays generic rather than
        // picking one kind's definition over the other three.
        tooltip: 'Workflows, Agent Graphs, Chains, and Runbooks — everything your team builds and versions.',
      },
      {
        // Label + aliases UNCHANGED (P2.432 adjustment): the lead's "merge
        // surfaces, not labels" ruling keeps this tab scoped to single-
        // workflow templates only — the automation/blueprint aliases moved
        // to the AI hub's "Use cases" tab below instead of living here.
        name: 'Templates',
        to: '/tenant/build/templates',
        aliases: ['template'],
        visible: (p) => p.isTenant,
        group: 'Reuse',
        tooltip: getBuildConcept('template')?.definition,
      },
      {
        name: 'Agents',
        to: '/tenant/build/agents',
        // 'people' kept searchable here (T-build-stage0 rename below):
        // renaming the "People & agents" group label to "AI agents" drops
        // the literal word "people" from the group header, so it's added
        // as a tab alias instead — same CommandPalette substring match
        // Definitions/navigation.ts already relies on for every other
        // retired label (see the top-of-file P2.435 comment).
        aliases: ['agent', 'bot', 'ai agent', 'people'],
        visible: (p) => roleIn(p, TENANT_RESOURCE_READERS),
        group: 'AI agents',
        tooltip: getBuildConcept('agent')?.definition,
      },
      {
        name: 'Skills',
        to: '/tenant/build/skills',
        aliases: ['skill'],
        visible: (p) => roleIn(p, TENANT_RESOURCE_READERS),
        group: 'AI agents',
        tooltip: getBuildConcept('skill')?.definition,
      },
      {
        name: 'Teams',
        to: '/tenant/build/teams',
        aliases: ['team'],
        visible: (p) => roleIn(p, TENANT_RESOURCE_READERS),
        group: 'AI agents',
        tooltip: getBuildConcept('team')?.definition,
      },
    ],
  },
  {
    // W11.2 (MONITOR-DOMAIN-DESIGN-SEP10.md): "Runs" renamed to "Monitor" —
    // Planning (Build) mutates definitions, Monitor observes/mutates
    // instances (today's workload, running/waiting/blocked/held, node
    // history). Route renamed from /tenant/automations; the old path now
    // redirects (see App.tsx's 'automations' loader).
    hubName: 'Monitor',
    hubTo: '/tenant/monitor',
    // Monitor-internal tabs (Today/All Runs/Activity/Schedules/Loops/
    // Calendars/System) are `?tab=` query-param state on ONE route, not real
    // routes — same reason this stayed empty under the old 'Runs' hubName.
    tabs: [],
  },
  {
    // Data hub (defect #6 / F1, AIRFLOW-TENANT-OWNER-TEST-OCT12.md): the
    // Airflow and Dagster consoles were unlisted standalone pages. They are
    // now tabs of one hub. Each tab keeps its own inner `?tab=` sub-tabs and
    // its own "not running on any of your clusters yet" empty state
    // (ADR-244), so the tab stays visible when the engine is absent. The
    // Jul 3 adjudication (see the Infra hub note) removed console tabs from
    // the ADMIN hub on persona-integrity grounds; this is a dedicated
    // operator/developer-facing hub, not that placement.
    hubName: 'Data',
    hubTo: '/tenant/data',
    tabs: [
      {
        name: 'Airflow',
        to: '/tenant/data/airflow',
        aliases: ['airflow', 'dag', 'dags', 'dag editor', 'airflow console', 'git sync'],
        // Reads here ARE enforced: routes.go gates them with
        // RequirePermission(read/list, airflow), and rbac.rego grants that to
        // these four tenant roles only. Was `p.isTenant`, so the billing,
        // finance, cost-centre and approval roles saw a tab that 403s.
        visible: (p) => roleIn(p, ['tenant_owner', 'tenant_admin', 'tenant_operator', 'tenant_viewer']),
        tooltip: 'Author, deploy and monitor Apache Airflow DAGs.',
      },
      {
        name: 'Dagster',
        to: '/tenant/data/dagster',
        aliases: ['dagster', 'asset', 'assets', 'materialization', 'dagster console'],
        visible: (p) => roleIn(p, TENANT_RESOURCE_READERS),
        tooltip: 'Dagster assets, materializations, checks and lineage.',
      },
    ],
  },
  {
    hubName: 'AI',
    hubTo: '/tenant/ai',
    tabs: [
      {
        name: 'Chat',
        to: '/tenant/ai/chat',
        aliases: ['chat', 'llm', 'direct llm', 'conversation'],
        visible: (p) => roleIn(p, TENANT_RESOURCE_READERS),
        // In-hub route since the AI-lands-on-Chat change (/tenant/chat
        // redirects here) — the AI nav highlight and hub tab bar now persist
        // in Chat like every other sub-section.
      },
      {
        name: 'Library',
        to: '/tenant/ai/library',
        aliases: ['library', 'catalog', 'marketplace'],
        visible: (p) => p.isTenant,
      },
      {
        name: 'MCP Tools',
        to: '/tenant/ai/mcp',
        aliases: ['mcp', 'tools', 'mcp tools', 'model context protocol'],
        visible: (p) => roleIn(p, TENANT_RESOURCE_READERS),
      },
      {
        name: 'Prompts',
        to: '/tenant/ai/prompts',
        aliases: ['prompt', 'prompt template'],
        visible: (p) => roleIn(p, TENANT_RESOURCE_READERS),
      },
      {
        // ADR-204 Wave A package 1 — tenant RAG on BYOK. Always shown to
        // any tenant (like the sibling tabs above); the tab's own page
        // renders the feature.tenant_rag not-enabled state rather than
        // hiding the nav entry, matching how the AI hub's other tabs never
        // condition their own tab-bar visibility on a tenant-tier flag.
        name: 'Knowledge Bases',
        to: '/tenant/ai/knowledge-bases',
        aliases: ['knowledge base', 'knowledge bases', 'rag', 'tenant rag', 'documents'],
        // The knowledgebase handler runs RequirePermission before serving; the
        // grant belongs to these four tenant roles. Was `p.isTenant`.
        visible: (p) => roleIn(p, ['tenant_owner', 'tenant_admin', 'tenant_operator', 'tenant_viewer']),
      },
      {
        name: 'AI Gateway',
        to: '/tenant/ai/gateway',
        aliases: ['ai gateway', 'ai models', 'models', 'providers'],
        // Narrowest of the four: RegisterSettingsRoutes gates this with
        // RequirePermission(list, ai_provider), which rbac.rego grants to
        // tenant_owner and tenant_admin ONLY — it was shown to all nine tenant
        // roles, so seven of them reached a page that refuses them.
        visible: (p) => roleIn(p, ['tenant_owner', 'tenant_admin']),
      },
      // Use cases (P2.426): replaces the two hardcoded tabs this hub used to
      // carry for exactly ONE use case (Lead Generation) — "Outbound
      // Campaigns" and "Leads". Both routes are KEPT (not removed) for deep
      // links and are still reachable/searchable via the aliases below; the
      // sub-nav entries themselves are gone. A future automation blueprint
      // needs a new descriptor row in configKindDescriptors.ts, never a new
      // tab here.
      {
        name: 'Use cases',
        to: '/tenant/ai/use-cases',
        aliases: [
          'use case',
          'use cases',
          // P2.432 adjustment (lead ruling, "merge surfaces not labels"):
          // this surface is absorbing the Templates gallery's "Automations"
          // section AND the Deployments audit page, so the generic
          // automation/blueprint/deployment vocabulary lives here, not on
          // the Build hub's Templates tab (which stays template-only).
          'automation',
          'automations',
          'blueprint',
          'blueprints',
          'deployments',
          'deployment log',
          // Carried over from the retired "Outbound Campaigns" tab so a
          // tenant searching an old name still finds the new home.
          'outbound campaigns',
          'outreach playbooks',
          'outbound playbook',
          'outbound',
          'lead gen',
          'lead generation',
          'campaigns',
          'sales playbook',
          'messaging campaign',
          // Carried over from the retired "Leads" tab.
          'leads',
          'lead list',
          'prospects',
          'outreach leads',
          'enrollments',
        ],
        visible: (p) => p.isTenant,
      },
    ],
  },
  {
    hubName: 'Governance',
    hubTo: '/tenant/governance',
    tabs: [
      {
        name: 'Approvals',
        to: '/tenant/governance/approvals',
        aliases: ['approval', 'approve', 'pending approvals'],
        visible: (p) => p.isTenant, // see APPROVALS_STACKED_ROLE_GAP below
      },
      {
        name: 'Guardrails',
        to: '/tenant/governance/guardrails',
        aliases: ['guardrail', 'safety', 'content policy'],
        // Mirrors the tenant roles on this route's RequireRole; see roleIn for the
        // platform-role rule. NOTE a live disagreement one layer down: rbac.rego
        // grants tenant_viewer guardrail read/list and useCapabilities mirrors that
        // in `guardrails:read`, while this route excludes the viewer. Siding with
        // the route is fail-closed and changes nothing a viewer could reach before,
        // but the route and the grant should be reconciled deliberately — the
        // route's stated reason (raw violation evidence) is already covered by the
        // separate guardrails:view_violations gate.
        visible: (p) => roleIn(p, ['tenant_owner', 'tenant_admin', 'tenant_operator']),
      },
      {
        name: 'Approval Gates',
        to: '/tenant/governance/approval-gates',
        aliases: ['approval gates', 'approval thresholds', 'enforce mode', 'second eye', 'separation of duties', 'second approver'],
        // Union of everyone who can change at least one control on the page
        // (canManageUsers covers tenant_owner/tenant_admin; approval_approver
        // can manage the second-eye toggle specifically — see
        // approvals:manage_second_eye in useCapabilities.ts).
        visible: (p) => p.isTenant && (p.canManageUsers || p.role === 'approval_approver'),
      },
      {
        name: 'Capabilities',
        to: '/tenant/governance/capabilities',
        aliases: ['capabilities', 'feature flags', 'tenant flags', 'reference nodes', 'checkpoint resume', 'handoffs'],
        // Mirrors RequireRole(['tenant_owner', 'tenant_admin', 'platform_admin']) —
        // now stated as the role list itself. The old form used canViewAudit for the
        // platform arm, which also covers platform_auditor, a role the route does
        // NOT admit — so auditors were shown this tab and then bounced.
        // Per-row write eligibility is server-computed, not re-derived here.
        visible: (p) => roleIn(p, ['tenant_owner', 'tenant_admin', 'platform_admin']),
      },
      {
        name: 'AI Governance',
        to: '/tenant/governance/ai',
        aliases: ['ai governance', 'governance dashboard'],
        visible: (p) => p.isTenant && p.canEditSettings,
      },
      {
        name: 'AI Council',
        to: '/tenant/governance/council',
        aliases: ['council', 'ai council'],
        visible: (p) =>
          // council:list, not canEditSettings — council/handler.go:267 says
          // "Live councils need council:list (every tenant role holds it)",
          // so gating a READ on an EDIT flag hid the tab from tenant_operator
          // and tenant_viewer, whom the server serves. Found by the
          // granted-and-hidden check; both reviewers predicted it.
          roleIn(p, TENANT_RESOURCE_READERS),
      },
      {
        name: 'Audit Log',
        to: '/tenant/governance/audit',
        aliases: ['audit', 'audit log', 'audit trail', 'compliance'],
        visible: (p) => p.isTenant && p.canViewAudit,
      },
      {
        name: 'DSR',
        to: '/tenant/governance/dsr',
        aliases: ['dsr', 'data subject request', 'gdpr', 'erasure'],
        visible: (p) => p.isTenant && p.canManageUsers,
      },
      {
        name: 'Sensitive Fields',
        to: '/tenant/governance/sensitive-fields',
        aliases: ['sensitive field', 'custom pii', 'field definitions', 'redaction', 'dlp'],
        // Mirrors the tenant roles on this route's RequireRole; mutations stay
        // admin-gated in-page. The route's own comment used to claim read was
        // "open to all tenant roles" — five are absent, and they were being shown
        // a tab that bounced them.
        visible: (p) =>
          roleIn(p, ['tenant_owner', 'tenant_admin', 'tenant_operator', 'tenant_viewer']),
      },
      {
        name: 'Compliance',
        to: '/tenant/governance/compliance',
        aliases: ['compliance', 'evidence', 'art22', 'eu ai act', 'iso 42001', 'nist rmf'],
        // Mirrors RequireRole(['tenant_owner', 'tenant_admin', 'platform_admin']) as
        // the role list itself; the old canViewAudit arm also admitted
        // platform_auditor, which the route rejects.
        //
        // NOT widened for compliance:read/art22:manage: the evidence, catalog
        // and sources handlers run their own role switch after the route's
        // RequirePermission (evidence_pack_handler.go:65), so a custom-role
        // grantee would be offered a tab whose every panel 403s.
        visible: (p) => roleIn(p, ['tenant_owner', 'tenant_admin', 'platform_admin']),
      },
      {
        name: 'Trust',
        to: '/tenant/governance/trust',
        aliases: ['trust', 'agent trust', 'trust score', 'earned autonomy', 'autonomy tier'],
        // Mirrors RequireRole(['tenant_owner', 'tenant_admin', 'platform_admin']) —
        // same gate as Compliance, stated as the role list for the same reason,
        // plus the capabilities the route now admits (see Compliance above).
        // trust:read only — a manage-only grantee is refused by the page's
        // own gate and by the server's list/read mapping.
        visible: (p) =>
          roleIn(p, ['tenant_owner', 'tenant_admin', 'platform_admin']) ||
          !!p.caps?.['trust:read'],
      },
      {
        name: 'Log Archive',
        to: '/tenant/governance/logs',
        aliases: ['log archive', 'archived logs', 'cold storage logs', 'task log archive'],
        // Mirrors RequireRole(['tenant_owner', 'tenant_admin', 'platform_admin']) in
        // App.tsx — same gate as Compliance/Trust above.
        visible: (p) => roleIn(p, ['tenant_owner', 'tenant_admin', 'platform_admin']),
      },
    ],
  },
  {
    hubName: 'Cost',
    hubTo: '/tenant/cost',
    // Cost already has its own CostSubNav pill tabs — no hub tabs needed.
    tabs: [],
  },
  {
    hubName: 'Infrastructure',
    hubTo: '/tenant/infra',
    tabs: [
      {
        name: 'Clusters',
        to: '/tenant/infra/clusters',
        aliases: ['cluster', 'k8s', 'kubernetes', 'byok'],
        // RequirePermission(read/list, cluster) is enforced in routes.go; the
        // same four tenant roles hold it. Was `p.isTenant`.
        visible: (p) => roleIn(p, ['tenant_owner', 'tenant_admin', 'tenant_operator', 'tenant_viewer']),
      },
      {
        name: 'Branch Deployments',
        to: '/tenant/infra/branch-deployments',
        aliases: ['branch', 'deployment', 'branch deployment'],
        // Mirrors the tenant roles on this route's RequireRole. Same unreconciled
        // disagreement as Guardrails: rbac.rego grants tenant_viewer
        // branch_deployment read/list while this route excludes it.
        visible: (p) => roleIn(p, ['tenant_owner', 'tenant_admin', 'tenant_operator']),
      },
      {
        name: 'Environments',
        to: '/tenant/infra/environments',
        aliases: ['environment', 'env'],
        visible: (p) => p.isTenant && p.canDeployWorkflow,
      },
      {
        name: 'Connections',
        to: '/tenant/infra/connections',
        aliases: ['connection', 'connect'],
        visible: (p) => p.isTenant && p.canDeployWorkflow,
      },
      {
        // Label only — route stays 'credentials' (P2.269 lead walkthrough,
        // refined item D). "Credentials" alone read like "my secrets" next
        // to the Connections/Secret Backends tabs; this tab is specifically
        // engine credentials (Airflow/Langflow/Agentic/Dagster), matching
        // the page's own "Engine Credentials" header.
        name: 'Engine Credentials',
        to: '/tenant/infra/credentials',
        aliases: ['credential', 'api key', 'key', 'engine credentials'],
        visible: (p) => p.isTenant && p.canDeployWorkflow,
      },
      {
        name: 'Secrets',
        to: '/tenant/infra/secrets',
        aliases: ['secret', 'credential', 'api key', 'password', 'ssh key'],
        visible: (p) => p.isTenant && p.canManageTenantResources,
      },
      {
        name: 'Secret Backends',
        to: '/tenant/infra/secret-backends',
        aliases: ['secret backend', 'vault', 'secret store'],
        visible: (p) => p.isTenant && p.canManageTenantResources,
      },
      {
        name: 'Notification Channels',
        to: '/tenant/infra/notification-channels',
        aliases: ['notification channel', 'channel', 'webhook'],
        visible: (p) => p.isTenant && p.canManageTenantResources,
      },
      {
        name: 'Archival',
        to: '/tenant/infra/archival',
        aliases: ['archival', 'archive', 'backup', 'backup and archival', 'retention'],
        visible: (p) => p.isTenant && p.canManageTenantResources,
      },
      // NOTE (design adjudication Jul 3): Airflow/Dagster console tabs were
      // briefly added here, then REMOVED per the placement-expert rubric —
      // consoles are Operator/Developer daily tools; routing them through an
      // admin hub fails persona integrity, and dual paths break nav coherence.
      // Console access = Runs-hub engine-card links (never health-gated) +
      // palette entries + direct routes. See roadmap consultation log.
    ],
  },
  {
    hubName: 'Admin',
    hubTo: '/tenant/admin',
    tabs: [
      {
        name: 'Users',
        to: '/tenant/admin/users',
        aliases: ['user', 'team member', 'member', 'people'],
        visible: (p) => p.isTenant && p.canManageUsers,
      },
      {
        name: 'Roles',
        to: '/tenant/admin/roles',
        aliases: ['role', 'permissions', 'capabilities', 'rbac'],
        visible: (p) => p.isTenant && p.canManageUsers,
      },
      {
        name: 'License',
        to: '/tenant/admin/license',
        aliases: ['license', 'plan', 'subscription'],
        // This route has NO RequireRole in App.tsx, so the real gate is the page's
        // own license:read capability (useCapabilities.ts): tenant_owner,
        // tenant_admin, tenant_operator, platform_admin. Was `p.isTenant`, which
        // showed it to tenant_viewer — whose "license" grant in rbac.rego is an
        // explicit empty list, so its panels render an error card. An unguarded
        // route admits everyone, which is why the route-guard comparison in
        // navigation.rbac.test.tsx cannot catch this shape; the capability must be
        // mirrored instead.
        visible: (p) => roleIn(p, ['tenant_owner', 'tenant_admin', 'tenant_operator']),
      },
      {
        name: 'SSO',
        // SSO stays at /tenant/settings/sso until ux-settings moves it (T2.3).
        to: '/tenant/settings/sso',
        aliases: ['sso', 'saml', 'oidc', 'single sign-on'],
        visible: (p) => p.isTenant && p.canEditSettings,
        external: true,
      },
      {
        name: 'Notification Rules',
        // N5: routed at /tenant/admin/notification-rules but had no tab —
        // the page was unreachable via UI. Same gate as its pre-T2 nav item.
        to: '/tenant/admin/notification-rules',
        aliases: ['routing rules', 'notification routing', 'rules'],
        visible: (p) => p.isTenant && p.canManageUsers,
      },
      {
        name: 'Tags',
        to: '/tenant/admin/tags',
        aliases: ['tag', 'label'],
        visible: (p) => p.isTenant,
      },
      {
        name: 'Categories',
        to: '/tenant/admin/categories',
        aliases: ['category'],
        visible: (p) => p.isTenant,
      },
      {
        name: 'Settings',
        // Settings regrouping is ux-settings work (T2.3) — link existing page.
        to: '/tenant/settings',
        aliases: ['settings', 'config', 'configuration', 'preferences'],
        visible: (p) => p.isTenant && p.canEditSettings,
        external: true,
      },
    ],
  },
  {
    hubName: 'Deleted',
    hubTo: '/tenant/deleted',
    // Recycle bin — rewired from one tab per soft-deletable entity type to a
    // single unified page (DeletedPage) covering all ~20 entity types with
    // in-page search + group filters (Build/AI/Infrastructure/Organize).
    // Empty tabs = "tabs belong to another agent" (see HubDef doc comment);
    // here it means the page owns its own in-page filtering instead of
    // route-based tabs. Gated on canManageTenantResources
    // (tenant_owner/tenant_admin/platform_admin), the same tier that gets
    // delete/archive/revoke actions elsewhere (see usePersona.ts's
    // canManageTenantResources doc comment) — the people who can delete a
    // resource are the ones who can restore it. Permanent delete within the
    // page is gated further, to tenant_owner only.
    tabs: [],
  },

  // ── Platform hubs (T-platform-IA) ───────────────────────────────────────────
  // Mirrors the tenant hub pattern: sidebar row + pill tabs, rather than the
  // old flat "Platform"/"Admin" sidebar sections. NOTE: hubName 'Cost' and
  // 'Admin' collide with the tenant hubs of the same name above — hub lookups
  // for these MUST match on `hubTo`, never on `hubName` alone.
  {
    hubName: 'Access',
    hubTo: '/platform/users',
    tabs: [
      {
        name: 'Platform Users',
        to: '/platform/users',
        aliases: ['platform user', 'staff', 'admin user'],
        visible: (p) =>
          // RequirePlatformRole() at the MOUNT (middleware/authz.go:667-669,
          // route_guards.golden:65-69) admits only these three, and it runs
          // BEFORE OPA. platform_debugger/observer/security_operator/sre hold
          // platform_user:list in rbac.rego and are still refused here, so the
          // policy golden alone gives a set that is too WIDE.
          roleIn(p, ['platform_admin', 'platform_operator', 'platform_viewer']),
      },
      {
        name: 'Approvals',
        to: '/platform/approvals',
        aliases: ['approval', 'approve', 'pending approvals'],
        visible: (p) => p.isPlatform && (p.canManageTenants || p.canManageUsers),
      },
      {
        name: 'Impersonation',
        to: '/platform/impersonation',
        aliases: ['impersonate', 'support access'],
        // support_rbac.rego's support_roles — the maker-checker impersonation-
        // grant surface (#97) is only meaningful to platform support staff.
        visible: (p) =>
          roleIn(p, ['platform_support_l2', 'platform_support_l3', 'platform_support_manager']),
      },
    ],
  },
  {
    // Platform → Catalog. Registries are the upstream sources whose listings
    // sync INTO the catalog (catalog_promoter.go), so they live as a tab of
    // the same hub rather than a separate sidebar entry.
    hubName: 'Catalog',
    hubTo: '/platform/catalog',
    tabs: [
      {
        name: 'Items',
        to: '/platform/catalog',
        end: true,
        aliases: ['catalog', 'platform catalog', 'catalog items'],
        visible: (p) =>
          // catalog:list, not canManageTenants — a MANAGE flag was gating a
          // READ, so platform_operator and platform_viewer were denied a tab
          // the server grants them (found by the granted-and-hidden check).
          roleIn(p, ['platform_admin', 'platform_operator', 'platform_viewer']),
      },
      {
        name: 'Registries',
        to: '/platform/catalog/registries',
        aliases: ['marketplace', 'marketplace registries', 'registry sync', 'smithery'],
        visible: (p) => p.isPlatform && p.canManageTenants,
      },
    ],
  },
  {
    hubName: 'Cost',
    hubTo: '/platform/cost',
    tabs: [
      {
        name: 'Dashboard',
        to: '/platform/cost',
        // Exact match: without it this tab also stays active on /platform/cost/chargeback, /cost-centers and /reports.
        end: true,
        aliases: ['cost dashboard'],
        visible: (p) => p.isPlatform && p.canViewCost,
      },
      {
        name: 'Chargeback',
        to: '/platform/cost/chargeback',
        aliases: ['chargeback'],
        visible: (p) => p.isPlatform && p.canViewCost,
      },
      {
        name: 'Cost Centers',
        to: '/platform/cost/cost-centers',
        aliases: ['cost center'],
        visible: (p) => p.isPlatform && p.canViewCost,
      },
      {
        name: 'Reports',
        to: '/platform/cost/reports',
        aliases: ['report', 'billing report'],
        visible: (p) => p.isPlatform && p.canViewCost,
      },
    ],
  },
  {
    hubName: 'Admin',
    hubTo: '/platform/settings',
    tabs: [
      {
        name: 'Settings',
        to: '/platform/settings',
        aliases: ['settings', 'config', 'configuration'],
        visible: (p) => p.isPlatform && (p.canEditSettings || p.canViewSettings),
      },
      {
        name: 'License',
        to: '/platform/license',
        aliases: ['license', 'plan'],
        visible: (p) =>
          roleIn(p, [
            'platform_admin',
            'platform_debugger',
            'platform_observer',
            'platform_operator',
            'platform_sre',
            'platform_support_l2',
            'platform_support_l3',
            'platform_viewer',
          ]),
      },
      {
        name: 'Feature Flags',
        to: '/platform/flags',
        aliases: ['feature flag', 'flag'],
        // Backend route (GET /platform/flags) is RequirePlatformAdmin-gated —
        // mirror that exactly rather than a broader capability check.
        visible: (p) => p.isPlatform && p.role === 'platform_admin',
      },
      {
        name: 'Archival Settings',
        to: '/platform/archival-settings',
        aliases: ['archival', 'retention', 'offboarding grace', 'dsr retention'],
        // Backend routes (/platform/archival-settings, RequirePlatformAdmin) are
        // platform_admin-only - mirror that exactly, same convention as Feature Flags.
        visible: (p) => p.isPlatform && p.role === 'platform_admin',
      },
      {
        name: 'Connector Store',
        to: '/platform/connector-store',
        aliases: ['connector', 'connectors', 'signing', 'definition signing', 'shared suffix', 'connector limits'],
        // /admin/connectors/* (limits, signing, shared catalog) is RequirePlatformAdmin-gated on the backend.
        visible: (p) => p.isPlatform && p.role === 'platform_admin',
      },
      {
        name: 'Template Catalog',
        to: '/platform/template-catalog',
        aliases: ['template', 'catalog', 'public template'],
        // /platform/template-catalog routes are RequirePlatformAdmin-gated.
        visible: (p) => p.isPlatform && p.role === 'platform_admin',
      },
      {
        name: 'Global Templates',
        to: '/platform/global-templates',
        aliases: ['global template', 'connection template', 'scaffold', 'legacy template', 'quarantine'],
        // /platform/global-templates routes are RequirePlatformAdmin-gated (ADR-292).
        visible: (p) => p.isPlatform && p.role === 'platform_admin',
      },
      {
        name: 'Secrets',
        to: '/platform/secrets',
        aliases: ['secret', 'credential', 'ai provider credential'],
        // Backend routes (RequirePlatformAdmin, /platform/secrets group,
        // internal/api/v1/platformsecret/handler.go) are platform_admin-only —
        // mirror that exactly, same convention as Feature Flags above.
        visible: (p) => p.isPlatform && p.role === 'platform_admin',
      },
      {
        name: 'Audit Log',
        to: '/platform/audit',
        aliases: ['audit', 'audit log', 'audit trail'],
        visible: (p) =>
          // The MOUNT decides this one, not the OPA grant. /platform/audit is
          // registered inside the platform-dashboard group, which is wrapped in
          // authorizer.RequirePlatformRole() (routes.go:1194-1197) - "the three
          // roles that may access platform dashboard read routes" - and
          // GetPlatformAudit repeats it via isPlatformUser
          // (platform/handler.go:1103-1111, audit_handler.go:43-46).
          //
          // It was `p.isPlatform && p.canViewAudit`. canViewAudit is
          // hasPermission(AUDIT_READ), and rbac.rego grants audit:read to 16
          // platform roles, so once the FE table was corrected to match policy
          // this tab appeared for 14 - of which 12 get a 403 at the mount and
          // PlatformAuditPage renders it as "unavailable". Fixing the
          // hidden-feature half created twelve dead tabs.
          roleIn(p, ['platform_admin', 'platform_operator', 'platform_viewer']),
      },
    ],
  },
]

// ── Navigation items (sidebar) ───────────────────────────────────────────────

/**
 * Authoritative sidebar navigation tree.
 *
 * Tenant: 9 hub items, no `section` so PersonaSidebar renders them as a
 * flat list without a section header. A hub is visible when the persona can
 * access at least one of its tabs; fine-grained gating happens at tab level.
 *
 * Platform: 8 hub items, same ungrouped treatment as tenant (T-platform-IA) —
 * Access/Cost/Admin are hubs with pill tabs (see hubDefinitions above); the
 * rest link directly to a single page.
 */
export const navigation: NavItem[] = [
  // ── Tenant: 8 hubs ──────────────────────────────────────────────────────────
  {
    name: 'Home',
    to: '/tenant/dashboard',
    icon: LayoutDashboard,
    section: 'Workspace',
    visible: (p) => p.isTenant,
  },
  {
    name: 'Build',
    to: '/tenant/build',
    icon: Workflow,
    section: 'Workspace',
    visible: (p) => p.isTenant,
  },
  {
    // W11.2: renamed from "Runs" to "Monitor" (MONITOR-DOMAIN-DESIGN-SEP10.md)
    // — route renamed from /tenant/automations to /tenant/monitor.
    name: 'Monitor',
    to: '/tenant/monitor',
    icon: Network,
    section: 'Workspace',
    visible: (p) => roleIn(p, TENANT_RESOURCE_READERS),
  },
  {
    name: 'Data',
    to: '/tenant/data',
    icon: Database,
    section: 'Workspace',
    visible: (p) => roleIn(p, TENANT_RESOURCE_READERS),
  },
  {
    name: 'Events',
    // Dependent-events console (ADR-141): recent ledger events + event triggers.
    to: '/tenant/events',
    icon: Radio,
    section: 'Workspace',
    visible: (p) => roleIn(p, TENANT_RESOURCE_READERS),
  },
  {
    name: 'AI',
    to: '/tenant/ai',
    icon: Cpu,
    section: 'Workspace',
    visible: (p) => p.isTenant,
  },
  {
    name: 'Governance',
    to: '/tenant/governance',
    icon: Shield,
    section: 'Manage',
    visible: (p) => p.isTenant, // see APPROVALS_STACKED_ROLE_GAP below
  },
  {
    name: 'Cost',
    to: '/tenant/cost',
    icon: DollarSign,
    section: 'Manage',
    visible: (p) => p.isTenant && p.canViewCost,
  },
  {
    name: 'Infrastructure',
    to: '/tenant/infra',
    icon: Server,
    section: 'Manage',
    // Derived from its tabs, like the Access hub. `p.isTenant` left the billing,
    // finance, cost-centre and approval roles looking at a hub whose every tab is
    // now gated away from them, and the hub index then sent them to Clusters,
    // which they cannot see. hubDefinitions stays the single source of truth.
    visible: (p) =>
      hubDefinitions.find((h) => h.hubTo === '/tenant/infra')!.tabs.some((t) => t.visible(p)),
  },
  {
    name: 'Admin',
    to: '/tenant/admin',
    icon: Settings,
    section: 'Manage',
    visible: (p) => p.isTenant,
  },
  {
    name: 'Deleted',
    to: '/tenant/deleted',
    icon: Trash2,
    visible: (p) => p.isTenant && p.canManageTenantResources,
    // Pinned to the sidebar footer, above the collapse control: the recycle
    // bin is a recovery destination visited rarely, so it does not belong in
    // the everyday scroll list. Same gate, same route, same active behaviour.
    footer: true,
  },
  {
    // The tenant-side twin of the platform notifications-inbox entry below:
    // App.tsx's 'notifications' route (bell "view all" page, old path) is
    // also not a sidebar destination. No `section` here (unlike the
    // platform entry) so the breadcrumb stays exactly as it already
    // resolved via the fallback path (tenantName > 'Notifications') —
    // this entry only fixes the browser tab title, which previously fell
    // through to the generic 'Dashboard' fallback in getPageTitle.
    name: 'Notifications',
    to: '/tenant/notifications',
    icon: Bell,
    visible: () => false,
  },

  // ── Platform: 8 hubs ────────────────────────────────────────────────────────
  {
    name: 'Platform Dashboard',
    to: '/platform',
    icon: LayoutDashboard,
    section: 'Platform',
    visible: (p) => p.isPlatform,
  },
  {
    name: 'Tenants',
    to: '/platform/tenants',
    icon: Building2,
    section: 'Platform',
    visible: (p) => p.isPlatform && !p.isAuditor,
  },
  {
    name: 'Managed Clusters',
    to: '/platform/managed/clusters',
    icon: Boxes,
    section: 'Platform',
    visible: (p) => p.isPlatform && p.canManageTenants,
  },
  {
    name: 'Catalog',
    to: '/platform/catalog',
    icon: LibraryBig,
    section: 'Platform',
    visible: (p) =>
          // catalog:list, not canManageTenants — a MANAGE flag was gating a
          // READ, so platform_operator and platform_viewer were denied a tab
          // the server grants them (found by the granted-and-hidden check).
          roleIn(p, ['platform_admin', 'platform_operator', 'platform_viewer']),
  },
  {
    name: 'Connector Types',
    to: '/platform/connector-types',
    icon: Plug,
    section: 'Platform',
    visible: (p) => p.isPlatform && p.canManageTenants,
  },
  {
    name: 'Broadcasts',
    to: '/platform/notifications',
    icon: Megaphone,
    section: 'Manage',
    visible: (p) => p.isPlatform && p.canManageTenants,
  },
  {
    // LIVE-25: the platform notifications INBOX (App.tsx's
    // 'notifications-inbox' route) is deliberately not a sidebar
    // destination — it's reached only via the bell's "View all
    // notifications" link, same as /tenant/notifications below. It still
    // needs an entry here so getPageTitle/getBreadcrumbs (both single-
    // sourced from this array, see file header) resolve it to "Notifications"
    // instead of falling through to the /platform prefix match ("Platform
    // Dashboard") — `visible: () => false` keeps it out of PersonaSidebar
    // and CommandPalette (CommandPalette only lists '/tenant/' items anyway)
    // while still giving the route a canonical name/section.
    name: 'Notifications',
    to: '/platform/notifications-inbox',
    icon: Bell,
    section: 'Platform',
    visible: () => false,
  },
  {
    name: 'Access',
    to: '/platform/users',
    icon: UserCog,
    section: 'Manage',
    // Hub visible when the persona can reach at least one of its tabs
    // (Platform Users / Approvals / Impersonation) — single source of truth
    // in hubDefinitions above, looked up by hubTo (hubName 'Access' is unique
    // but we match the same way as Cost/Admin below for consistency).
    visible: (p) =>
      hubDefinitions.find((h) => h.hubTo === '/platform/users')!.tabs.some((t) => t.visible(p)),
  },
  {
    name: 'Cost',
    to: '/platform/cost',
    icon: DollarSign,
    section: 'Manage',
    visible: (p) => p.isPlatform && p.canViewCost,
  },
  {
    name: 'Admin',
    to: '/platform/settings',
    icon: Settings,
    section: 'Manage',
    visible: (p) =>
      // Was `p.isPlatform`, which is the stranded-hub shape #842 fixed on
      // /tenant/data and /tenant/governance. platform_auditor holds only
      // AUDIT_READ and AUDIT_EXPORT in the permission table, so Audit Log was
      // its ONLY Admin tab; once that tab correctly follows the mount (which
      // refuses the auditor) the hub had no reachable tab and the sweep's
      // "a hub is shown exactly when one of its tabs is reachable" check
      // caught it.
      //
      // OPEN POLICY QUESTION, deliberately not settled here: ADR-0040 defines
      // platform_auditor as read-only across all tenants, so arguably it
      // SHOULD be admitted to the platform audit metadata endpoint. That is a
      // server-side change to RequirePlatformRole's list, not a nav change.
      // Until someone makes it, the nav tells the truth: the mount refuses it.
      p.isPlatform && !p.isAuditor,
  },
]

// ── Derived utilities ─────────────────────────────────────────────────────────

/**
 * Returns true when the given nav item is a strict prefix of another item in the
 * set (e.g. /platform is a prefix of /platform/tenants). NavLink uses `end` in
 * that case so only the more-specific child link activates on child routes.
 */
export function navItemNeedsExactMatch(item: NavItem, allItems: NavItem[] = navigation): boolean {
  return allItems.some((other) => other.to !== item.to && other.to.startsWith(item.to + '/'))
}

/**
 * Precomputed set of hrefs that need NavLink `end` matching. Computed once at
 * module load from the navigation constant — no per-render O(n²) scan.
 */
export const exactMatchHrefs: ReadonlySet<string> = new Set(
  navigation.filter((item) => navItemNeedsExactMatch(item)).map((item) => item.to),
)

/**
 * Longest-prefix match of a pathname against the navigation tree.
 * Shared by Header breadcrumbs and useTenantPageTitle so page titles and
 * breadcrumbs can never drift from the sidebar.
 */
export function findNavItemByPath(pathname: string): NavItem | null {
  let best: NavItem | null = null
  for (const item of navigation) {
    if (pathname === item.to || pathname.startsWith(item.to + '/')) {
      if (!best || item.to.length > best.to.length) best = item
    }
  }
  return best
}

/**
 * Longest-prefix match of a pathname against all hub tab definitions.
 * Used by Header to build a two-level breadcrumb (hub > tab) for hub
 * sub-pages without duplicating route strings anywhere.
 */
export function findHubTabByPath(pathname: string): { hub: HubDef; tab: HubTab } | null {
  let best: { hub: HubDef; tab: HubTab; len: number } | null = null
  for (const hub of hubDefinitions) {
    for (const tab of hub.tabs) {
      if (pathname === tab.to || pathname.startsWith(tab.to + '/')) {
        const len = tab.to.length
        if (!best || len > best.len) best = { hub, tab, len }
      }
    }
  }
  return best ? { hub: best.hub, tab: best.tab } : null
}

/**
 * True when a route path segment is an opaque identifier — a UUID (any
 * version/variant), a 24-char hex id (Mongo-style ObjectId), or a 26-char
 * ULID — rather than a human-readable slug.
 *
 * Used to stop the breadcrumb/title fallback in Header.tsx from
 * "humanising" an id by splitting on dashes and Title-Casing each hex chunk
 * (P2.147 — a loop's own console page at
 * `/tenant/loops/64d19940-dc3b-4a26-99d4-b6a6ccc4d7eb` rendered its
 * breadcrumb as "64d19940 Dc3b 4a26 99d4 B6a6ccc4d7eb" because `loops/:id`
 * was never added to `dynamicRoutes`, so the pathname fell through to the
 * last-resort "capitalise the last segment" rule). Any `/tenant/<feature>/:id`
 * route not yet listed in `dynamicRoutes` hits this same fallback, so the
 * check is generic rather than route-specific.
 */
export function isIdLikeSegment(segment: string): boolean {
  if (!segment) return false
  // UUID: 8-4-4-4-12 hex groups (case-insensitive, any version/variant byte).
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(segment)) return true
  // 24-char hex id (Mongo-style ObjectId).
  if (/^[0-9a-f]{24}$/i.test(segment)) return true
  // ULID: 26-char Crockford base32 (excludes I, L, O, U).
  if (/^[0-9A-HJKMNP-TV-Z]{26}$/i.test(segment)) return true
  return false
}

/**
 * Turns a plural path segment (possibly hyphenated, e.g. "notification-channels")
 * into a singular, title-cased resource label ("Notification Channel"). Used by
 * the breadcrumb fallback to label an id-shaped segment generically when no
 * `dynamicRoutes` entry exists for its route (see `isIdLikeSegment` above).
 */
export function singularizeSegmentLabel(segment: string): string {
  const words = segment.split('-').filter(Boolean)
  if (words.length === 0) return 'Item'
  const lastWord = words[words.length - 1]
  const singularLast =
    lastWord.length > 1 && lastWord.endsWith('s') && !lastWord.endsWith('ss')
      ? lastWord.slice(0, -1)
      : lastWord
  return [...words.slice(0, -1), singularLast]
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
}

// ── Breadcrumb derivation (moved here from Header.tsx so getBreadcrumbs can
//    be exported without tripping react-refresh/only-export-components,
//    which requires component files to export components only) ─────────────

// Dynamic route patterns for detail pages that have :id segments.
// These are NOT in the nav tree (they're child pages, not top-level items).
// Parent paths now point to the canonical hub routes after T2 migration.
const dynamicRoutes: { prefix: string; title: string; parent: string; parentTitle: string }[] = [
  // P2.435 follow-up: parentTitle tracks the renamed Build-hub tab
  // ("Definitions", was "Workflows") — the leaf title stays 'Workflow
  // Detail' since the record being viewed is still specifically a workflow.
  // Create pages first (startsWith, first match wins): `/new` and `/generate`
  // are not records, so they get their own leaf instead of "<Kind> Detail".
  { prefix: '/tenant/build/workflows/new', title: 'New Workflow', parent: '/tenant/build/workflows', parentTitle: 'Definitions' },
  { prefix: '/tenant/build/agent-graphs/new', title: 'New Agent Graph', parent: '/tenant/build/agent-graphs', parentTitle: 'Agent Graphs' },
  { prefix: '/tenant/build/agent-graphs/generate', title: 'Generate Agent Graph', parent: '/tenant/build/agent-graphs', parentTitle: 'Agent Graphs' },
  { prefix: '/tenant/build/agent-graphs/campaigns', title: 'Send Campaigns', parent: '/tenant/build/agent-graphs', parentTitle: 'Agent Graphs' },
  { prefix: '/tenant/build/chains/generate', title: 'Generate Chain', parent: '/tenant/build/chains', parentTitle: 'Chains' },
  { prefix: '/tenant/build/runbooks/new', title: 'New Runbook', parent: '/tenant/build/runbooks', parentTitle: 'Runbooks' },
  { prefix: '/tenant/build/workflows/', title: 'Workflow Detail', parent: '/tenant/build/workflows', parentTitle: 'Definitions' },
  { prefix: '/tenant/build/agent-graphs/', title: 'Agent Graph Detail', parent: '/tenant/build/agent-graphs', parentTitle: 'Agent Graphs' },
  { prefix: '/tenant/build/chains/', title: 'Chain Detail', parent: '/tenant/build/chains', parentTitle: 'Chains' },
  { prefix: '/tenant/build/runbooks/', title: 'Runbook Detail', parent: '/tenant/build/runbooks', parentTitle: 'Runbooks' },
  { prefix: '/tenant/build/templates/', title: 'Template Detail', parent: '/tenant/build/templates', parentTitle: 'Templates' },
  { prefix: '/tenant/build/agents/', title: 'Agent Detail', parent: '/tenant/build/agents', parentTitle: 'Agents' },
  { prefix: '/tenant/build/skills/', title: 'Skill Detail', parent: '/tenant/build/skills', parentTitle: 'Skills' },
  { prefix: '/tenant/build/teams/', title: 'Team Detail', parent: '/tenant/build/teams', parentTitle: 'Teams' },
  { prefix: '/tenant/ai/prompts/', title: 'Prompt Detail', parent: '/tenant/ai/prompts', parentTitle: 'Prompts' },
  { prefix: '/tenant/ai/library/', title: 'Library Item', parent: '/tenant/ai/library', parentTitle: 'Library' },
  { prefix: '/tenant/governance/council/', title: 'Council Detail', parent: '/tenant/governance/council', parentTitle: 'AI Council' },
  { prefix: '/tenant/governance/audit/', title: 'Audit Detail', parent: '/tenant/governance/audit', parentTitle: 'Audit Log' },
  { prefix: '/tenant/governance/approvals/', title: 'Approval Detail', parent: '/tenant/governance/approvals', parentTitle: 'Approvals' },
  { prefix: '/tenant/infra/clusters/', title: 'Cluster Detail', parent: '/tenant/infra/clusters', parentTitle: 'Clusters' },
  { prefix: '/platform/tenants/', title: 'Tenant Detail', parent: '/platform/tenants', parentTitle: 'Tenants' },
  { prefix: '/platform/managed/clusters/', title: 'Cluster Detail', parent: '/platform/managed/clusters', parentTitle: 'Managed Clusters' },
  // W11.2: Runs hub renamed to Monitor, route moved to /tenant/monitor.
  { prefix: '/tenant/executions/', title: 'Execution Detail', parent: '/tenant/monitor', parentTitle: 'Monitor' },
  // Engine run detail (/tenant/runs/{engine}/…, features/engine-runs) — without
  // this the crumb humanised the raw run id (live 2026-09-06: 'B41e40e4 9452 …').
  { prefix: '/tenant/runs/', title: 'Run Detail', parent: '/tenant/monitor', parentTitle: 'Monitor' },
  // P2.147: loop/schedule detail pages (App.tsx's `loops/:id` / `schedules/:id`,
  // rendered directly — not redirects) were missing here, so their crumb fell
  // through to the raw-UUID fallback (live dev-india 2026-09-11: a loop console
  // page showed "64d19940 Dc3b 4a26 99d4 B6a6ccc4d7eb"). Both are Monitor tabs
  // (query-param state, not real routes — see hubDefinitions' Monitor entry).
  { prefix: '/tenant/loops/', title: 'Loop Detail', parent: '/tenant/monitor', parentTitle: 'Monitor' },
  { prefix: '/tenant/schedules/', title: 'Schedule Detail', parent: '/tenant/monitor', parentTitle: 'Monitor' },
  // Support ticket detail (/tenant/support/tickets/:id) — same gap as above.
  { prefix: '/tenant/support/tickets/', title: 'Support Ticket Detail', parent: '/tenant/support/tickets', parentTitle: 'My Tickets' },
  // Chat session detail (in-hub since the AI-lands-on-Chat change)
  { prefix: '/tenant/ai/chat/', title: 'AI Chat', parent: '/tenant/ai/chat', parentTitle: 'Chat' },
  // SSO sub-pages: exact-path entries so the leaf shows the sub-page name
  { prefix: '/tenant/settings/sso/access-review', title: 'Access Review', parent: '/tenant/settings/sso', parentTitle: 'SSO' },
  { prefix: '/tenant/settings/sso/drift', title: 'SSO Drift Report', parent: '/tenant/settings/sso', parentTitle: 'SSO' },
]

export interface Breadcrumb {
  label: string
  path?: string // undefined = current page (no link rendered)
}

/**
 * Derives breadcrumbs from this file (single source of truth).
 *
 * Priority order:
 * 1. Dynamic routes — detail pages with :id segments (not in nav tree).
 * 2. Hub tab match via findHubTabByPath — gives a two-level crumb
 *    (hub name > tab name) without duplicating any route string.
 * 3. Longest-prefix nav item match — ordinary hub/platform items.
 * 4. Fallback — capitalise the last path segment, UNLESS it's an opaque id
 *    (see isIdLikeSegment), in which case label it generically instead of
 *    humanising the id itself (P2.147).
 */
export function getBreadcrumbs(pathname: string, isPlatform: boolean, tenantName?: string): Breadcrumb[] {
  const crumbs: Breadcrumb[] = []

  // Root context crumb
  if (!isPlatform && tenantName) {
    crumbs.push({ label: tenantName, path: '/tenant/dashboard' })
  }

  // 1. Dynamic routes — detail pages not in the nav tree
  for (const dr of dynamicRoutes) {
    if (pathname.startsWith(dr.prefix)) {
      const parentItem = navigation.find((item) => item.to === dr.parent)
      if (parentItem?.section) {
        crumbs.push({ label: parentItem.section })
      } else {
        // Parent is a hub tab (or a retired tab's route), not a flat nav item:
        // lead with the hub crumb ("Build > Definitions > ...") so detail and
        // create pages read the same as the hub's own pages.
        const hub = hubDefinitions.find((h) => h.hubTo === '/tenant/build' && dr.parent.startsWith(h.hubTo + '/'))
        const hubItem = hub ? navigation.find((item) => item.to === hub.hubTo) : undefined
        if (hubItem) crumbs.push({ label: hubItem.name, path: hubItem.to })
      }
      crumbs.push({ label: dr.parentTitle, path: dr.parent })
      crumbs.push({ label: dr.title })
      return crumbs
    }
  }

  // 2. Hub tab match — two-level breadcrumb: Hub > Tab
  const hubTabMatch = findHubTabByPath(pathname)
  if (hubTabMatch) {
    const hubItem = navigation.find((item) => item.to === hubTabMatch.hub.hubTo)
    if (hubItem) crumbs.push({ label: hubItem.name, path: hubItem.to })
    crumbs.push({ label: hubTabMatch.tab.name })
    return crumbs
  }

  // 3. Longest-prefix match in the nav tree
  const sorted = [...navigation].sort((a, b) => b.to.length - a.to.length)
  const matched = sorted.find(
    (item) => pathname === item.to || pathname.startsWith(item.to + '/')
  )

  if (matched) {
    if (matched.section) {
      crumbs.push({ label: matched.section })
    }
    crumbs.push({ label: matched.name })
    return crumbs
  }

  // 4. Fallback — capitalise the last path segment, unless it's an opaque id.
  //    A route not yet listed in dynamicRoutes/the nav tree must still never
  //    render a raw UUID/ObjectId/ULID as its page name (P2.147): label it
  //    with its singularised parent segment + a short id instead.
  const segments = pathname.split('/').filter(Boolean)
  const lastSegment = segments[segments.length - 1] ?? ''
  if (isIdLikeSegment(lastSegment)) {
    const parentSegment = segments[segments.length - 2]
    const resourceLabel = parentSegment ? singularizeSegmentLabel(parentSegment) : 'Item'
    crumbs.push({ label: `${resourceLabel} ${lastSegment.slice(0, 8)}` })
    return crumbs
  }
  const label = lastSegment
    .replace(/-/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
  crumbs.push({ label: label || 'Dashboard' })
  return crumbs
}
